import { Button } from "@dropbox/dig-components/buttons";
import { Truncate } from "@dropbox/dig-components/truncate";
import { Text } from "@dropbox/dig-components/typography";
import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { CalendarResponse } from "@mirage/mosaics/Chat/components/chat/calendar/CalendarResponse";
import type { CalendarChatJson } from "@mirage/mosaics/Chat/components/chat/calendar/types";
import { ConversationMessageActionContextView } from "@mirage/mosaics/Chat/components/chat/ConversationMessages/ConversationMessageActionContextView";
import type {
  LayoutVariant,
  RenderMessageActions,
} from "@mirage/mosaics/Chat/components/chat/ConversationMessages/types";
import type { MarkdownResponseProps } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownResponse";
import { MarkdownResponse } from "@mirage/mosaics/Chat/components/chat/markdown/MarkdownResponse";
import type { ChatConversationMessageMessage } from "@mirage/mosaics/Chat/types";
import { openURL } from "@mirage/service-platform-actions";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { memo, useCallback } from "react";
import styles from "./ConversationMessages.module.css";

export interface ConversationMessageRowActionAndTextProps {
  variant: LayoutVariant;
  message: ChatConversationMessageMessage;
  isLastMessage?: boolean;
  isInProgress: boolean;
  renderActions?: RenderMessageActions;
  onOpenArtifact: () => void;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
  overrideMarkdownRenderer?: (props: MarkdownResponseProps) => React.ReactNode;
  showDraftGeneratedButton?: boolean;
  spanId?: string;
}

/**
 * Content of a message row, showing its associated action and response text.
 */
export const ConversationMessageRowActionAndText = memo(
  ({
    variant,
    message,
    isInProgress,
    renderActions,
    onOpenArtifact,
    logComposeEvent,
    conversationId,
    searchSessionId,
    assistRequestId,
    overrideMarkdownRenderer,
    showDraftGeneratedButton,
    spanId,
  }: ConversationMessageRowActionAndTextProps) => {
    const handleMarkdownContainerClicks = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (target.tagName === "A") {
          e.preventDefault();
          openURL((target as HTMLAnchorElement).href);
        }
      },
      [],
    );

    const getActionContextText = () => {
      switch (message.actionContext?.type) {
        case "draft_generated":
          return i18n.t("compose_assistant_draft_generated");
        case "template_generated":
          return i18n.t("assist_template_created_message");
        case "template_updated":
          return i18n.t("assist_template_updated_message");
        case "tone_generated":
          return i18n.t("compose_tone_generated_message");
        case "tone_updated":
          return i18n.t("compose_tone_updated_message");
        default:
          return null;
      }
    };

    const actionContextText = getActionContextText();
    // this is a special case for messages of type draft_generated that have an exact text match
    // of draft generated in which case we should just hide the message text — this should never happen
    // going forward but ensures back compatibility to remove duplicate messages in old sessions
    const isDraftGeneratedMsgAndContext =
      message.actionContext?.type === "draft_generated" &&
      message.text === i18n.t("compose_assistant_draft_generated");
    const body =
      message.role === "user" ? (
        <div className={styles.ConversationMessageRowPlainText}>
          {message.text}
        </div>
      ) : (
        // disabling eslint rule because this is not actually acting as a link
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          onClick={handleMarkdownContainerClicks}
          className={classNames(styles.ConversationMessageRowMarkdown, {
            [styles.ConversationMessageRowMarkdownCondensed]:
              variant === "condensed",
          })}
        >
          {actionContextText && (
            <Text
              size={variant === "condensed" ? "medium" : "large"}
              color="subtle"
              className={styles.ConversationMessageActionContextText}
            >
              {actionContextText}
            </Text>
          )}
          {/* Render markdown response if the message does NOT have JSON data
          ...this is a temporary fix. I need to look into why message.text is
          populated with JSON on re-render but not on initial render */}
          {!isDraftGeneratedMsgAndContext &&
            !message.json &&
            (overrideMarkdownRenderer ? (
              overrideMarkdownRenderer({
                markdownText: message.text,
                referencingSources: message.referencingSources,
                logComposeEvent,
                conversationId,
                searchSessionId,
                assistRequestId,
              })
            ) : (
              <>
                <MarkdownResponse
                  className={styles.MarkdownResponse}
                  markdownText={message.text}
                  referencingSources={message.referencingSources}
                  logComposeEvent={logComposeEvent}
                  conversationId={conversationId}
                  searchSessionId={searchSessionId}
                  assistRequestId={assistRequestId}
                />
                {showDraftGeneratedButton && (
                  <Button
                    className={styles.OpenDraftButton}
                    variant="opacity"
                    size="small"
                    onClick={onOpenArtifact}
                  >
                    {i18n.t("compose_open_draft")}
                  </Button>
                )}
              </>
            ))}
        </div>
      );
    const contextViewProps = {
      variant,
      message,
      onOpenArtifact,
      logComposeEvent,
    };
    const actionsElement = renderActions?.(message);

    // Calendar Responses have custom UI that should not wrapped as a message text
    if (message.json?.type === "calendar") {
      return (
        <CalendarResponse
          calendarChat={message?.json.calendarEvents as CalendarChatJson[]}
          conversationId={conversationId || ""}
          logComposeEvent={logComposeEvent}
          spanId={spanId}
        />
      );
    }

    // TODO:(rich) better general representation of sub-messages
    // certain action types have specific layouts, e.g. dash search includes a starting message + submessage regarding the queries
    if (message.actionContext) {
      switch (message.actionContext?.type) {
        case "dash_search":
          return (
            <>
              <div className={styles.ConversationMessageRowMessageActionText}>
                <Text
                  size={variant === "condensed" ? "medium" : "large"}
                  color="subtle"
                >
                  {isInProgress
                    ? i18n.t("assistant_message_searching")
                    : i18n.t("assistant_message_searched")}
                  :
                </Text>
              </div>
              <div className={styles.ConversationMessageRowMessageActionText}>
                <div>
                  <ConversationMessageActionContextView
                    {...contextViewProps}
                    conversationId={conversationId}
                    searchSessionId={searchSessionId}
                    assistRequestId={assistRequestId}
                  />
                  {actionsElement}
                </div>
              </div>
            </>
          );
        case "dash_search_analyze":
          return (
            <div className={styles.ConversationMessageRowMessageActionText}>
              <div>
                <Text
                  size={variant === "condensed" ? "medium" : "large"}
                  color="subtle"
                >
                  {isInProgress
                    ? i18n.t("assistant_message_analyzing_content", {
                        totalCount: message.referencingSources?.length || 0,
                      })
                    : i18n.t("assistant_message_analyzed_content", {
                        totalCount: message.referencingSources?.length || 0,
                      })}
                  :
                </Text>
                <ConversationMessageActionContextView
                  {...contextViewProps}
                  conversationId={conversationId}
                  searchSessionId={searchSessionId}
                  assistRequestId={assistRequestId}
                />
                {actionsElement}
              </div>
            </div>
          );
        case "read_current_browser_tab":
          return (
            <div className={styles.ConversationMessageRowMessageActionText}>
              <Text
                size={variant === "condensed" ? "medium" : "large"}
                color="subtle"
              >
                <Truncate
                  lines={2}
                  tooltipControlProps={{ auto: true, placement: "top" }}
                >
                  {i18n.t("compose_message_title_read_browser_tab", {
                    title: message.actionContext.sourceTitle,
                  })}
                </Truncate>
              </Text>
              <Text size={variant === "condensed" ? "medium" : "large"}>
                {body}
              </Text>
              <ConversationMessageActionContextView
                variant={variant}
                message={message}
                onOpenArtifact={onOpenArtifact}
                logComposeEvent={logComposeEvent}
                conversationId={conversationId}
                searchSessionId={searchSessionId}
                assistRequestId={assistRequestId}
              />
              {actionsElement}
            </div>
          );
        case "done":
        case "error":
        case "reading":
        case "compose_selection_edit":
        case "draft_generated":
        case "template_generated":
        case "template_updated":
        case "template_selected":
        case "tone_generated":
        case "tone_updated":
        case "tone_selected":
          // these types don't have a specific layout, fall through to use normal message layout
          break;
        default:
          message.actionContext satisfies never;
          throw new Error(
            `Unknown action context type: ${message.actionContext}`,
          );
      }
    }

    return (
      <div
        className={classNames(styles.ConversationMessageRowMessageActionText, {
          [styles.ConversationMessageRowMessageAnimation]:
            message.role !== "user",
        })}
      >
        <Text size={variant === "condensed" ? "medium" : "large"}>{body}</Text>
        <ConversationMessageActionContextView
          {...contextViewProps}
          conversationId={conversationId}
          searchSessionId={searchSessionId}
          assistRequestId={assistRequestId}
        />
      </div>
    );
  },
);
ConversationMessageRowActionAndText.displayName =
  "ConversationMessageRowActionAndText";
